import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Sentry } from "@/helpers/sentry";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

export interface ErrorFallbackProps {
  error: Error;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
  resetError: () => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, eventId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
    this.setState({ errorInfo, eventId });
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, eventId: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          eventId={this.state.eventId}
          resetError={this.resetError}
        />
      );
    }
    return this.props.children;
  }
}

/* ─── Default Fallback UI ─── */

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const { t } = useTranslation(['auth']);
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      title={t('auth:errorBoundary.copyToClipboard')}
      className={`inline-flex items-center gap-1 border rounded px-2 py-0.5 text-[10px] cursor-pointer transition-all duration-150 bg-transparent border-white/12 ${copied ? 'text-green-300/80' : 'text-white/35'}`}
    >
      {copied ? t('auth:errorBoundary.copied') : t('auth:errorBoundary.copy')}
    </button>
  );
};

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  eventId,
}) => {
  const { t } = useTranslation(['auth']);
  const [showDetails, setShowDetails] = React.useState(false);
  const handleGoHome = () => { window.location.href = "/"; };
  const handleReload = () => { window.location.reload(); };
  const handleLogout = () => { window.location.href = "/logout"; };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden z-9999 bg-[linear-gradient(145deg,#0c0e14_0%,#111827_50%,#0f1219_100%)]">
      {/* Animated background orbs — styled via index.css .eb-orb-* */}
      <div className="eb-orb-1" />
      <div className="eb-orb-2" />
      <div className="eb-orb-3" />

      {/* Card — styled via index.css .eb-container */}
      <div className="eb-container">
        {/* Icon */}
        <div className="eb-icon-wrapper">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="eb-icon"
          >
            <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Text */}
        <h1 className="text-[22px] font-semibold text-white/92 tracking-[-0.02em] leading-snug mb-2">
          {t('auth:errorBoundary.title')}
        </h1>
        <p className="text-sm text-white/45 mb-6 leading-relaxed">
          {t('auth:errorBoundary.subtitle')}
        </p>

        {/* Error message preview */}
        <div className="bg-red-500/6 border border-red-500/12 rounded-lg px-4 py-2.5 mb-3">
          <code className="text-xs text-red-400 font-mono wrap-break-word leading-relaxed">
            {error.message || t('auth:errorBoundary.unknownError')}
          </code>
        </div>

        {/* Reference ID for support */}
        {eventId && (
          <div className="flex items-center justify-between gap-2 mb-7 px-1">
            <span className="text-[11px] text-white/35 uppercase tracking-wider">
              {t('auth:errorBoundary.reference')}
            </span>
            <div className="flex items-center gap-2">
              <code className="text-[11px] text-white/60 font-mono">{eventId}</code>
              <CopyButton text={eventId} />
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-3 gap-2.5 mb-5">
          <button
            onClick={handleGoHome}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[13px] font-medium text-white/55 bg-white/4 border border-white/12 rounded-lg cursor-pointer transition-all duration-200 hover:bg-white/8 hover:border-white/25 hover:-translate-y-px"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            {t('auth:errorBoundary.goHome')}
          </button>
          <button
            onClick={handleReload}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[13px] font-medium text-white/55 bg-white/4 border border-white/12 rounded-lg cursor-pointer transition-all duration-200 hover:bg-white/8 hover:border-white/25 hover:-translate-y-px"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6" />
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M3 22v-6h6" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
            {t('auth:errorBoundary.reloadPage')}
          </button>
          <button
            onClick={handleLogout}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[13px] font-medium text-red-500/70 bg-red-500/6 border border-red-500/20 rounded-lg cursor-pointer transition-all duration-200 hover:bg-red-500/15 hover:border-red-500/50 hover:text-red-400 hover:-translate-y-px"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('auth:errorBoundary.logout')}
          </button>
        </div>

        {/* Expandable stack trace */}
        <button
          onClick={() => setShowDetails((prev) => !prev)}
          className="inline-flex items-center gap-1.5 bg-transparent border-0 text-white/30 text-xs cursor-pointer px-2 py-1 transition-colors duration-200 hover:text-white/60"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform duration-250 ${showDetails ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
          {showDetails ? t('auth:errorBoundary.hideErrorDetails') : t('auth:errorBoundary.showErrorDetails')}
        </button>

        {showDetails && (
          <div className="mt-4 text-left animate-[eb-fade-in_0.3s_ease-out]">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30">
                  {t('auth:errorBoundary.errorLabel')}
                </span>
                <CopyButton text={error.stack || error.message} />
              </div>
              <pre className="eb-stack-trace">{error.stack || error.message}</pre>
            </div>
            {errorInfo?.componentStack && (
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30">
                    {t('auth:errorBoundary.componentStackLabel')}
                  </span>
                  <CopyButton text={errorInfo.componentStack.trim()} />
                </div>
                <pre className="eb-stack-trace">
                  {errorInfo.componentStack.trim()}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorBoundary;
